import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Collapse, IconButton, Box } from '@mui/material';
import { ArrowDownward, ArrowUpward, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { formatNumber } from '../../../../utils/helper';
import { StyledNARTDCell, StyledNARTDRow, TableContainerSection } from './SKUTable.Styled';

interface Pack {
  pack_segment: string;
  transactions: number;
  transaction_growth: number;
  vol_uc: number;
  vol_growth: number;
  nr: number;
  contri_to_nr: number;
  contri_to_nr_country: number;
  nr_by_vol_uc: number;
  nr_by_vol_uc_index_to_all_india: number;
}

interface CategoryData {
  category: string;
  transactions: number;
  transaction_growth: number;
  vol_uc: number;
  vol_growth: number;
  nr: number;
  contri_to_nr: number;
  contri_to_nr_country: number;
  nr_by_vol_uc: number;
  nr_by_vol_uc_index_to_all_india: number;
  pack: Pack[];
}

interface NestedTableProps {
  data: CategoryData[];
  toggle: string;
}

const NestedTable: React.FC<NestedTableProps> = ({ data, toggle }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data.length > 0) {
      setExpandedRows(new Set([data[0].category]));
    }
  }, [data]);

  const handleExpandClick = (category: string) => {
    setExpandedRows((prev) => {
      const newExpandedRows = new Set(prev);
      if (newExpandedRows.has(category)) {
        newExpandedRows.delete(category);
      } else {
        newExpandedRows.add(category);
      }
      return newExpandedRows;
    });
  };

  const sortedFilteredData = [...data];

  const nartdRow = sortedFilteredData.find((row) => row.category === 'NARTD');
  const otherRows = sortedFilteredData.filter((row) => row.category !== 'NARTD');

  const headerCellStyle = {
    borderRight: '1px solid #ddd',
    overflow: 'hidden',
    fontSize: '10.14px',
    fontWeight: 600,
    maxHeight: '10px',
    padding: '0px',
    align: 'center',
    textAlign: 'center',
    whiteSpace: 'break-elipses',

    height: '34.47px',
    lineHeight: '12.36px',
    background: '#F4F4F4',
    paddingLeft: '10px',
    paddingRight: '10px',
    width: '60px',
    borderBottom: '1.01px solid #7D7D7D',
  };

  const bodyCellStyle = {
    borderRight: '1px solid #ddd',
    overflow: 'hidden',
    fontSize: '9.08px',
    fontWeight: 500,
    maxHeight: '22.88px !important',
    height: '22.88px !important',
    minHeight: '22.88px !important',
    padding: '0px',
    align: 'center',
    textAlign: 'center',
    whiteSpace: 'break-elipses',

    lineHeight: '11.07px',
    width: '60px',
    borderBottom: '0.91px solid rgba(234, 234, 234, 0.3)',
  };

  const renderTableHeader = () => {
    const headers =
      toggle === 'NIELSEN'
        ? [
            { label: 'Category X Pack', width: '110px' },
            { label: 'Value Share' },
            { label: 'Change - Value Share' },
            { label: 'ND%' },
            { label: 'ND% Change' },
            { label: 'Comp ND%' },
            { label: 'PPL' },
            { label: 'PPL Index vs Category', width: '100px' },
          ]
        : [
            { label: 'Category X Pack', width: '110px' },
            { label: 'Vol(UC)' },
            { label: 'Volume Growth' },
            { label: 'NR' },
            { label: '% Contri to NR' },
            { label: '% Contri to NR India' },
            { label: 'NR/UC' },
            { label: 'NR/UC Indexed to all India', width: '100px' },
          ];

    return headers.map(({ label, width }) => (
      <TableCell
        key={label}
        sx={{
          ...headerCellStyle,
          ...(width && { width }),
        }}
      >
        {label}
      </TableCell>
    ));
  };

  const renderTableBody = (row: any, type: any) => {
    const nartdValues = data.find((item) => item.category === 'NARTD');
    const nartdDefaults = {
      contri_to_nr: nartdValues ? nartdValues.contri_to_nr : 1,
      contri_to_nr_country: nartdValues ? nartdValues.contri_to_nr_country : 1,
      nr_by_vol_uc_index_to_all_india: nartdValues ? nartdValues.nr_by_vol_uc_index_to_all_india : 1,
      value_share: 1,
    };

    const fields =
      toggle === 'NIELSEN'
        ? [
            {
              key: 'value_share',
              label: 'Value Share',
              isPercent: true,
              isBar: true,
              baseValue: nartdDefaults.value_share,
            },
            {
              key: 'value_share_change',
              label: 'Change - Value Share',
              isPercent: true,
              condition: (value: number) => (value > 0 ? 'green' : value < 0 ? 'red' : 'black'),
              arrow: true,
            },
            { key: 'nd', label: 'ND%', isPercent: true },
            {
              key: 'nd_change',
              label: 'ND% Change',
              isPercent: true,
              condition: (value: number) => (value > 0 ? 'green' : value < 0 ? 'red' : 'black'),
              arrow: true,
            },
            { key: 'nd_comp', label: 'Comp ND%', isPercent: true },
            { key: 'ppl', label: 'PPL' },
            {
              key: 'price_premiumness_Index',
              label: 'PPL Index vs Category',
              isWide: true,
              condition: (value: number) => (value > nartdDefaults.value_share ? 'green' : 'red'),
            },
          ]
        : [
            { key: 'vol_uc', label: 'Vol(UC)' },
            {
              key: 'vol_growth',
              label: 'Volume Growth',
              isPercent: true,
              condition: (value: number) => (value > 0 ? 'green' : value < 0 ? 'red' : 'black'),
              arrow: true,
            },
            { key: 'nr', label: 'NR' },
            {
              key: 'contri_to_nr',
              label: '% Contri to NR',
              isPercent: true,
              isBar: true,
              baseValue: nartdDefaults.contri_to_nr,
            },
            {
              key: 'contri_to_nr_country',
              label: '% Contri to NR India',
              isPercent: true,
              isBar: true,
              baseValue: nartdDefaults.contri_to_nr_country,
            },
            { key: 'nr_by_vol_uc', label: 'NR/UC' },
            {
              key: 'nr_by_vol_uc_index_to_all_india',
              label: 'NR/UC Indexed to all India',
              isWide: true,
              condition: (value: number) => (value > nartdDefaults.nr_by_vol_uc_index_to_all_india ? 'green' : 'red'),
            },
          ];

    return fields.map(({ key, isPercent, isWide, isBar, baseValue, condition, arrow }) => {
      const cellValue = row[key];
      const percentage = isPercent ? cellValue * 100 : cellValue;
      const conditionResult = condition && typeof condition === 'function' ? condition(cellValue) : null;
      return (
        <TableCell
          key={key}
          style={{
            color: condition && typeof condition === 'function' ? condition(cellValue) : 'black',
          }}
          sx={{
            ...bodyCellStyle,
            ...(isWide && { width: '100px' }),
          }}
        >
          {isBar ? (
            <div
              style={{
                display: 'grid',
                alignItems: 'center',
                gridTemplateColumns: '50% 50%',
              }}
            >
              <div
                style={{
                  width:
                    key === 'contri_to_nr' || key === 'contri_to_nr_country' || key === 'value_share'
                      ? `${percentage / baseValue}% `
                      : `${percentage / baseValue}%`,
                  height: '8px',
                  backgroundColor: type === 'child' ? 'rgba(232, 28, 14, 0.54)' : '#DF2020',
                  marginRight: '5px',
                }}
              ></div>
              <span>{formatNumber(percentage, 1)}%</span>
            </div>
          ) : key === 'nr_by_vol_uc_index_to_all_india' || key === 'price_premiumness_Index' ? (
            <div
              style={{
                display: 'inline-block',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor:
                  row.category !== 'NARTD'
                    ? conditionResult === 'green'
                      ? 'rgba(144, 238, 144, 0.2)'
                      : 'rgba(255, 182, 193, 0.2)'
                    : 'transparent',
                color: row.category !== 'NARTD' ? (conditionResult === 'green' ? 'green' : 'red') : 'black',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              {formatNumber(cellValue, 2)}
            </div>
          ) : arrow && (key === 'vol_growth' || key === 'value_share_change' || key === 'nd_change') ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '30% 70%',
                padding: '0px 10px',
              }}
            >
              {percentage >= 0 ? (
                <ArrowUpward
                  style={{
                    color: '#02AD7B',
                    border: '1px solid #ECFCF3',
                    background: '#ECFCF3',
                    borderRadius: '50%',
                    fontSize: '0.7rem',
                    marginRight: '5px',
                  }}
                />
              ) : percentage < 0 ? (
                <ArrowDownward
                  style={{
                    color: '#EA4335',
                    border: '1px solid rgba(254, 175, 136, 0.2)',
                    background: 'rgba(254, 175, 136, 0.2)',
                    borderRadius: '50%',
                    fontSize: '0.7rem',
                    marginRight: '5px',
                  }}
                />
              ) : (
                <div></div>
              )}
              <span>{`${formatNumber(percentage, 1)}%`}</span>
            </div>
          ) : isPercent ? (
            `${formatNumber(percentage, 1)}%`
          ) : (
            formatNumber(cellValue, 1)
          )}
        </TableCell>
      );
    });
  };

  return (
    <TableContainerSection>
      <Table
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          minWidth: 400,
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#F4F4F4',
            }}
          >
            {renderTableHeader()}
          </TableRow>
        </TableHead>

        <TableBody>
          {otherRows.map((row) => {
            const isExpanded = expandedRows.has(row.category);
            return (
              <React.Fragment key={row.category}>
                <TableRow
                  sx={{
                    height: '20.88px',
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableCell
                    style={{
                      color: '#DF2020',
                      fontSize: 10,
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      paddingLeft: '10px',
                    }}
                    sx={{ ...bodyCellStyle, width: '110px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <span>{row.category}</span>
                      <IconButton
                        style={{
                          color: '#DF2020',
                          width: '10px',
                          height: '12px',
                          marginLeft: '5px',
                          fontSize: '1rem',
                          textAlign: 'right',
                        }}
                        onClick={() => handleExpandClick(row.category)}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUp style={{ fontSize: '1rem' }} />
                        ) : (
                          <KeyboardArrowDown style={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  {renderTableBody(row, 'parent')}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: 0 }}>
                    <Collapse in={isExpanded}>
                      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 400 }}>
                        <TableBody>
                          {row.pack.map((packRow) => (
                            <TableRow
                              key={packRow.pack_segment}
                              sx={{
                                backgroundColor: '#ffffff',
                                height: '20.88px',
                              }}
                            >
                              <TableCell
                                sx={{ ...bodyCellStyle, width: '110px', textAlign: 'right', paddingRight: '15px' }}
                              >
                                {packRow.pack_segment}
                              </TableCell>
                              {renderTableBody(packRow, 'child')}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}

          {nartdRow && (
            <StyledNARTDRow>
              <StyledNARTDCell sx={{ ...bodyCellStyle, width: '110px' }}>{nartdRow.category}</StyledNARTDCell>
              {renderTableBody(nartdRow, 'parent')}
            </StyledNARTDRow>
          )}
        </TableBody>
      </Table>
    </TableContainerSection>
  );
};

export default NestedTable;
